import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "@/lib/cn";

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & { hint?: string }
>(({ className, children, hint, ...props }, ref) => (
  <div className="mb-1.5">
    <LabelPrimitive.Root
      ref={ref}
      className={cn("block text-sm font-medium text-neutral-700", className)}
      {...props}
    >
      {children}
    </LabelPrimitive.Root>
    {hint && <p className="mt-0.5 text-xs text-neutral-400">{hint}</p>}
  </div>
));
Label.displayName = "Label";

/**
 * Field — a label + control + optional hint, used for essentially every
 * form input in the admin app (~50 call sites across Content Editor,
 * Settings, Users, Variables, etc.).
 *
 * Audit finding M7: this used to render the label text and `children` as
 * SIBLINGS with no `htmlFor`/`id` pairing between them — visually adjacent,
 * but programmatically unassociated. A screen-reader user tabbing into any
 * of these inputs heard nothing announced, and clicking the label text did
 * nothing (only `Login.tsx` wired up explicit `htmlFor`/`id` pairs; every
 * other field in the app did not).
 *
 * Fixed by nesting `children` INSIDE the `<label>` element instead —
 * Radix's `LabelPrimitive.Root` explicitly supports this as an alternative
 * to `htmlFor`/`id` (it's built to handle a nested interactive descendant
 * correctly, e.g. not double-firing on pointer-down), and native HTML
 * "implicit labeling" then associates the label with the first labelable
 * descendant (input, textarea, select, button) regardless of which control
 * component Field happens to wrap — Input/Textarea (real DOM elements),
 * Select (a Radix trigger `<button>`), and Combobox (a plain `<button>`
 * with no `id` prop to inject) are all covered by this, without needing to
 * clone `children` or know which one is "the real input." A `<label>`
 * carries no font/size/color styling itself (only the inner label-text
 * `<span>` does) specifically so nesting an Input inside it can't
 * accidentally make typed text inherit bold label styling.
 *
 * `htmlFor` is still accepted for a caller that already manages its own
 * matching `id` (none currently do outside `Login.tsx`, which uses the
 * plain `Label` component directly, not `Field`) — passing both is valid
 * per the HTML spec and never sets up an ambiguous double-association here
 * since this component controls both sides.
 */
export function Field({
  label,
  hint,
  children,
  htmlFor,
  className,
  action,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  htmlFor?: string;
  className?: string;
  /** A control shown at the right end of the label row (outside the <label>, so clicking it never focuses the input). */
  action?: React.ReactNode;
}) {
  return (
    <div className={cn("relative mb-4", className)}>
      {action && <div className="absolute right-0 top-[-3px] z-10">{action}</div>}
      <LabelPrimitive.Root htmlFor={htmlFor} className="block">
        <span className="mb-1.5 block text-sm font-medium text-neutral-700">{label}</span>
        {children}
      </LabelPrimitive.Root>
      {hint && <p className="mt-1.5 text-xs text-neutral-400">{hint}</p>}
    </div>
  );
}
